// utils/api/enhancedApiService.ts
import { apiHandler } from './responseHandler';
import type { APIResponse, APIRequestConfig } from '../../types/quoter/types';
import type { UserInfo } from '../auth/authTypes';
import { useAuth } from '../../hooks/auth/useAuth';

/**
 * React hook that provides API service with automatic user headers
 * Use this in components instead of apiHandler.fetch()
 */
export const useApiService = () => {
    const { user } = useAuth();

    // Create API methods that automatically include user headers
    const apiService = {
        async fetch<T>(url: string, config: APIRequestConfig = {}): Promise<APIResponse<T>> {
            // Prepare user headers
            const userHeaders: Record<string, string> = {};

            if (user && user.userId) {
                userHeaders['X-User-Id'] = String(user.userId);
                userHeaders['X-User-Type-Id'] = String(user.userTypeId || 10);
            } else {
                // Anonymous user defaults
                userHeaders['X-User-Id'] = '0';
                userHeaders['X-User-Type-Id'] = '10';
            }

            // Merge user headers with any existing headers
            const enhancedConfig: APIRequestConfig = {
                ...config,
                headers: {
                    ...userHeaders,
                    ...config.headers // Allow overriding if needed
                }
            };

            return apiHandler.fetch<T>(url, enhancedConfig);
        },

        async get<T>(url: string, config: Omit<APIRequestConfig, 'method'> = {}): Promise<APIResponse<T>> {
            return this.fetch<T>(url, { ...config, method: 'GET' });
        },

        async post<T>(url: string, data?: any, config: Omit<APIRequestConfig, 'method' | 'body'> = {}): Promise<APIResponse<T>> {
            return this.fetch<T>(url, {
                ...config,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...config.headers
                },
                body: data ? JSON.stringify(data) : undefined
            });
        },

        async put<T>(url: string, data?: any, config: Omit<APIRequestConfig, 'method' | 'body'> = {}): Promise<APIResponse<T>> {
            return this.fetch<T>(url, {
                ...config,
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...config.headers
                },
                body: data ? JSON.stringify(data) : undefined
            });
        },

        async delete<T>(url: string, config: Omit<APIRequestConfig, 'method'> = {}): Promise<APIResponse<T>> {
            return this.fetch<T>(url, { ...config, method: 'DELETE' });
        }
    };

    return apiService;
};